/**
 * TodaysStarWidget — /  homepage
 * A daily featured entry from the Nature Atlas. Picks a deterministic
 * "star" per day so every visitor sees the same subject and the site
 * feels curated rather than shuffled. Zero new backend — pulls from
 * the existing `/api/nature/atlas/entities` list.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ArrowRight, Sparkles, Leaf } from "lucide-react";
import { http } from "@/lib/api";

function _dayKey() {
  const d = new Date();
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}
// Deterministic FNV-1a → integer hash. Same day → same star.
function _hash(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}

export default function TodaysStarWidget() {
  const [pool, setPool]  = useState([]);
  const [star, setStar]  = useState(null);
  const [busy, setBusy]  = useState(true);

  // Step 1: load the pool of populated entity slugs.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await http.get("/nature/atlas/entities?limit=200&has_body=true");
        if (!cancelled) setPool(data.items || []);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Step 2: once we have a pool, deterministically pick + fetch details.
  useEffect(() => {
    if (pool.length === 0) { setBusy(false); return; }
    const h = _hash(String(_dayKey()));
    const picked = pool[h % pool.length];
    let cancelled = false;
    (async () => {
      try {
        const { data } = await http.get(`/nature/atlas/entities/${encodeURIComponent(picked.slug)}`);
        if (!cancelled) setStar(data);
      } catch { if (!cancelled) setStar(picked); }
      finally { if (!cancelled) setBusy(false); }
    })();
    return () => { cancelled = true; };
  }, [pool]);

  if (busy || !star) return null;   // silent if empty; not a blocker
  const b = star.body || {};
  const summary =
    b.short_summary ||
    (b.overview || "").split(/(?<=\. )/)[0] ||   // first sentence
    "";

  return (
    <section className="max-w-[1400px] mx-auto px-6 lg:px-10 my-10"
             data-testid="todays-star">
      <div className="rounded-3xl overflow-hidden border shadow-sm"
           style={{ borderColor: "var(--wofy-border)" }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          {/* Photo */}
          <div className="relative aspect-video md:aspect-auto md:h-full bg-black/5">
            <img src={star.photo_url} alt={star.name}
                 className="w-full h-full object-cover"
                 data-testid="todays-star-photo" />
            <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-white text-[11px] font-mono uppercase tracking-wider"
                 style={{ background: "rgba(217,119,6,0.92)" }}>
              <Star className="w-3 h-3 fill-white" /> Today's Star
            </div>
          </div>

          {/* Content */}
          <div className="p-6 sm:p-8 flex flex-col justify-center"
               style={{ background: "linear-gradient(135deg,#FEF9F0,#FEF3C7)" }}>
            <div className="overline mb-1" style={{ color: "#B25D38" }}>
              <Sparkles className="w-3 h-3 inline mr-1" />
              A DAILY WONDER FROM NATURE PULSE
            </div>
            <h2 className="font-display text-3xl sm:text-4xl leading-tight"
                data-testid="todays-star-name">
              {star.name}
            </h2>
            {star.scientific_name && (
              <div className="italic font-mono text-sm opacity-80 mt-1">
                {star.scientific_name}
              </div>
            )}
            {summary && (
              <p className="mt-3 opacity-90 leading-relaxed" data-testid="todays-star-summary">
                {summary.length > 320 ? summary.slice(0, 320).replace(/\s+\S*$/, "") + "…" : summary}
              </p>
            )}
            <div className="mt-5 flex flex-wrap gap-2">
              <Link to={`/nature/species/${star.slug}`}
                    className="wofy-btn-primary text-sm"
                    data-testid="todays-star-view">
                Read the full profile <ArrowRight className="w-3 h-3" />
              </Link>
              <Link to={`/nature/atlas/${star.category}`}
                    className="wofy-btn-ghost text-sm"
                    data-testid="todays-star-category">
                <Leaf className="w-3 h-3" /> More {star.category?.replace(/-/g, " ")}
              </Link>
            </div>
            <div className="text-[11px] opacity-60 mt-4">
              A new star arrives every 24 hours — come back tomorrow.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
